import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, ArrowUpRightSquare } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * MembershipGate — a banner shown at the top of the Partner Dashboard when a
 * user's membership_status is not 'active'. Explains the withdrawal lock and
 * routes them to the purchase page. Returns null when active so it disappears
 * silently for paying partners.
 */
export default function MembershipGate() {
  const { user } = useAuth();
  const status = user?.membership_status || "inactive";
  if (status === "active") return null;

  if (status === "pending") {
    return (
      <div
        className="glass-card border border-[#FBBF24]/40 bg-[#FBBF24]/[0.04] p-5 flex items-start gap-4"
        data-testid="membership-gate-pending"
      >
        <Clock size={22} className="text-[#FBBF24] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-display font-black text-white">Your Business Starter Kit is under review.</div>
          <div className="text-sm text-white/70 mt-1">
            Admin approval typically lands within one business day. Once approved, your commissions become withdrawable.
          </div>
        </div>
        <Link
          to="/membership/purchase"
          className="text-xs px-3 py-2 rounded-md border border-[#FBBF24] text-[#FBBF24] hover:bg-[#FBBF24] hover:text-[#0F2042] uppercase tracking-widest font-bold inline-flex items-center gap-1"
          data-testid="gate-pending-view"
        >
          View Status
        </Link>
      </div>
    );
  }

  return (
    <div
      className="glass-card border-2 border-[#FBBF24]/60 bg-[#FBBF24]/[0.05] p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4"
      data-testid="membership-gate-inactive"
    >
      <AlertTriangle size={26} className="text-[#FBBF24] shrink-0" />
      <div className="flex-1">
        <div className="font-display font-black text-white text-lg">Activate your membership to unlock withdrawals.</div>
        <div className="text-sm text-white/75 mt-1 leading-relaxed">
          Commissions and referral bonuses are accruing to your Commission Wallet, but Zenith holds
          all withdrawals until you purchase and activate a Business Starter Kit. Pick a kit to get started.
        </div>
      </div>
      <Link
        to="/membership/purchase"
        className="btn-deploy inline-flex items-center gap-2 whitespace-nowrap"
        data-testid="gate-inactive-cta"
      >
        <ArrowUpRightSquare size={14} /> Choose Kit
      </Link>
    </div>
  );
}
